import React from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, BookOpen, Sparkles } from 'lucide-react';
import NewsletterSignup from '../components/NewsletterSignup';
import SEO from '../components/SEO';
import { useLang } from '../context/LangContext';

const DAILY_PROMPTS = [
  // Pattern 1: Source Beyond Comprehension
  { pattern: 1, en: 'What is one thing you feel deeply but cannot put into words?', cn: '有什么事情你深有感触却无法用言语表达？' },
  { pattern: 1, en: 'When was the last time you felt awe — real, wordless awe?', cn: '你上一次感到真正的、无法言说的敬畏是什么时候？' },
  { pattern: 1, en: 'If the source of everything is beyond language, what does your silence already know?', cn: '如果万物的源头超越语言，你的沉默已经知道什么？' },
  { pattern: 1, en: 'What mystery are you most comfortable not solving?', cn: '你最能坦然面对的未解之谜是什么？' },
  { pattern: 1, en: 'Close your eyes for 10 seconds. What did you notice?', cn: '闭上眼睛10秒钟。你注意到了什么？' },
  { pattern: 1, en: 'What feels sacred to you, even if you can\'t explain why?', cn: '什么对你来说是神圣的，即使你无法解释原因？' },
  { pattern: 1, en: 'If you could ask the universe one question, what would it be?', cn: '如果你能问宇宙一个问题，那会是什么？' },
  // Pattern 2: Consciousness Is Fundamental
  { pattern: 2, en: 'Right now, who is reading these words? Not your name — who is the one aware?', cn: '此刻，是谁在读这些文字？不是你的名字——那个觉知者是谁？' },
  { pattern: 2, en: 'When you close your eyes, what remains that is not a thought?', cn: '当你闭上眼睛，剩下什么不是念头的东西？' },
  { pattern: 2, en: 'What changed in you last year that you only noticed this year?', cn: '去年你有什么变化是今年才注意到的？' },
  { pattern: 2, en: 'If awareness came before matter, what does that mean for how you spend your morning?', cn: '如果意识先于物质，这对你如何度过早晨意味着什么？' },
  { pattern: 2, en: 'What is your mind doing right now, and who is watching it do that?', cn: '你的心现在在做什么，是谁在看着它这样做？' },
  { pattern: 2, en: 'What is one thing you\'re certain you didn\'t choose to think today?', cn: '今天有什么念头你确定不是自己选择去想的？' },
  { pattern: 2, en: 'Are you the thinker, or the space in which thoughts appear?', cn: '你是思考者，还是念头出现的空间？' },
  // Pattern 3: Connection Over Separation
  { pattern: 3, en: 'Who is one person you feel disconnected from? What would change if you saw them as yourself?', cn: '你跟谁感到疏离？如果你把他们看作你自己，什么会改变？' },
  { pattern: 3, en: 'What is one way you contributed to someone else\'s day today — even without knowing it?', cn: '今天你以什么方式为别人的一天做出了贡献——即使你不知道？' },
  { pattern: 3, en: 'When you feel most alone, what are you separating yourself from?', cn: '当你感到最孤独的时候，你在把自己与什么分开？' },
  { pattern: 3, en: 'What would you do differently today if you truly believed nothing is separate?', cn: '如果你真的相信没有什么是分离的，今天你会有什么不同？' },
  { pattern: 3, en: 'Name three things that had to connect for you to exist.', cn: '说出三个必须相连才能让你存在的事物。' },
  { pattern: 3, en: 'What boundary in your life might actually be a bridge?', cn: '你生活中的什么界限实际上可能是一座桥？' },
  { pattern: 3, en: 'Who would you call if you had five minutes left?', cn: '如果你只剩五分钟，你会打给谁？' },
  // Pattern 4: Ego Is the Root of Suffering
  { pattern: 4, en: 'What story is your ego telling you right now that isn\'t actually true?', cn: '你的自我现在正在告诉你什么其实不是真的故事？' },
  { pattern: 4, en: 'When did you last compare yourself to someone? What was the ego protecting?', cn: '你上次跟别人比较是什么时候？自我在保护什么？' },
  { pattern: 4, en: 'What would you do today if no one would ever know about it?', cn: '如果没有人会知道，你今天会做什么？' },
  { pattern: 4, en: 'What are you defending that doesn\'t actually need defending?', cn: '你在捍卫什么实际上不需要捍卫的东西？' },
  { pattern: 4, en: 'Who are you when you stop trying to be someone?', cn: '当你停止试图成为某人时，你是谁？' },
  { pattern: 4, en: 'What would change if you treated your next frustration as information, not an attack?', cn: '如果你把下一次挫败当作信息而非攻击，什么会改变？' },
  { pattern: 4, en: 'What opinion are you holding onto just because letting go feels like losing?', cn: '你抓住什么观点不放，仅仅因为放手感觉像是失去？' },
  // Pattern 5: Letting Go Is Freedom
  { pattern: 5, en: 'What is one thing you could release today that would make tomorrow lighter?', cn: '今天你可以放下什么，让明天更轻松？' },
  { pattern: 5, en: 'What outcome are you gripping so tightly it\'s causing you stress?', cn: '你紧抓着什么结果不放，以至于给自己造成了压力？' },
  { pattern: 5, en: 'If surrender is not weakness but the ultimate strength — what would you surrender right now?', cn: '如果臣服不是软弱而是终极力量——你现在会臣服于什么？' },
  { pattern: 5, en: 'What would your day look like if you stopped forcing things?', cn: '如果你不再强求事物，你的一天会是什么样？' },
  { pattern: 5, en: 'Is there a grudge you\'re carrying that weighs more than the person who caused it?', cn: '你是否在背负一个比造成它的人更沉重的怨恨？' },
  { pattern: 5, en: 'What expectation could you drop right now?', cn: '你现在可以放下什么期望？' },
  { pattern: 5, en: 'What did letting go teach you the last time you managed to do it?', cn: '上次你成功放手时，放手教了你什么？' },
  // Pattern 6: The Present Moment
  { pattern: 6, en: 'What are you missing right now because your mind is somewhere else?', cn: '因为你的心在别处，你现在正在错过什么？' },
  { pattern: 6, en: 'Take three slow breaths. What shifted?', cn: '做三次缓慢的深呼吸。什么变了？' },
  { pattern: 6, en: 'What sound can you hear right now that you weren\'t noticing until this question?', cn: '你现在能听到什么声音是在这个问题之前没有注意到的？' },
  { pattern: 6, en: 'Where is your body holding tension that your mind hasn\'t acknowledged?', cn: '你身体的哪个部位承载着你的心灵尚未承认的紧张？' },
  { pattern: 6, en: 'What if this moment — exactly as it is — is enough?', cn: '如果这一刻——恰好是现在这样——就已经足够了呢？' },
  { pattern: 6, en: 'What is one thing you can see right now that you\'ve never truly looked at?', cn: '你现在能看到什么是你从未真正注视过的？' },
  { pattern: 6, en: 'The past is memory. The future is imagination. What is actually here?', cn: '过去是记忆。未来是想象。实际上在这里的是什么？' },
  // Pattern 7: What Remains Is Love
  { pattern: 7, en: 'When you strip away everything you do, everything you own, everything you know — what remains?', cn: '当你去掉你做的一切、拥有的一切、知道的一切——剩下什么？' },
  { pattern: 7, en: 'Who do you love without any conditions, and what does that tell you about yourself?', cn: '你无条件地爱着谁，这告诉你关于自己什么？' },
  { pattern: 7, en: 'When was the last time you felt compassion for a stranger?', cn: '你上次对一个陌生人感到同情是什么时候？' },
  { pattern: 7, en: 'What if love is not something you find, but something you already are?', cn: '如果爱不是你要找的东西，而是你本来就是的呢？' },
  { pattern: 7, en: 'What is one small act of kindness you could do in the next hour?', cn: '接下来一个小时你可以做一件什么小小的善事？' },
  { pattern: 7, en: 'Who needs your attention more than your phone does right now?', cn: '现在谁比你的手机更需要你的关注？' },
  { pattern: 7, en: 'What would the world look like if everyone you met today felt genuinely seen by you?', cn: '如果你今天遇到的每个人都真正感到被你看见，世界会是什么样？' },
];

const PATTERN_LABELS = {
  1: { en: 'Source', cn: '源头' },
  2: { en: 'Consciousness', cn: '意识' },
  3: { en: 'Connection', cn: '连接' },
  4: { en: 'Ego', cn: '自我' },
  5: { en: 'Letting Go', cn: '放下' },
  6: { en: 'Presence', cn: '当下' },
  7: { en: 'Love', cn: '爱' },
};

function getDailyPrompt() {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 0);
  const dayOfYear = Math.floor((now - start) / 86400000);
  return DAILY_PROMPTS[dayOfYear % DAILY_PROMPTS.length];
}

export default function Home() {
  const { lang } = useLang();
  const daily = getDailyPrompt();
  const promptText = lang === 'cn' ? daily.cn : daily.en;
  const patternLabel = PATTERN_LABELS[daily.pattern]?.[lang] || PATTERN_LABELS[daily.pattern]?.en;

  return (
    <div className="min-h-screen">
      <SEO path="/" />
      {/* Hero */}
      <section className="min-h-screen flex flex-col items-center justify-center px-6 pt-16 relative overflow-hidden">
        {/* Warm candle-like radial glow */}
        <div className="absolute inset-0 pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse at 50% 55%, rgba(149,107,68,0.10) 0%, rgba(80,57,38,0.05) 40%, transparent 70%)',
          }}
        />

        <div className="relative text-center max-w-2xl mx-auto animate-fade-in-up">
          <p className="text-3xl mb-6 tracking-[0.3em]"
            style={{ color: 'rgba(201, 168, 76, 0.45)' }}>
            万灯一光
          </p>

          <h1 className="font-serif text-4xl md:text-6xl tracking-wider leading-tight mb-6"
            style={{ color: '#f7f7f4' }}>
            ONE LIGHT,
            <br />
            MANY LAMPS
          </h1>

          <p className="text-lg md:text-xl font-serif italic mb-12"
            style={{ color: 'rgba(212, 197, 171, 0.65)' }}>
            Where Ancient Wisdom Meets the Age of AI
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
            <Link
              to="/ask"
              className="inline-flex items-center justify-center gap-2 px-8 py-3 transition-all rounded-sm text-sm tracking-wide"
              style={{
                backgroundColor: 'rgba(149, 107, 68, 0.12)',
                border: '1px solid rgba(149, 107, 68, 0.35)',
                color: '#c9a84c',
              }}
              onMouseEnter={e => {
                e.target.style.backgroundColor = 'rgba(149, 107, 68, 0.22)';
                e.target.style.borderColor = 'rgba(149, 107, 68, 0.55)';
              }}
              onMouseLeave={e => {
                e.target.style.backgroundColor = 'rgba(149, 107, 68, 0.12)';
                e.target.style.borderColor = 'rgba(149, 107, 68, 0.35)';
              }}
            >
              <MessageCircle size={16} />
              Ask a Question
            </Link>
            <Link
              to="/book"
              className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-transparent transition-all rounded-sm text-sm tracking-wide"
              style={{
                border: '1px solid rgba(212, 197, 171, 0.2)',
                color: 'rgba(212, 197, 171, 0.6)',
              }}
            >
              <BookOpen size={16} />
              Get the Book
            </Link>
          </div>
        </div>
      </section>

      {/* Seven Patterns intro */}
      <section className="py-24 px-6">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="font-serif text-2xl md:text-3xl mb-8"
            style={{ color: '#eae1d3' }}>
            Seven Patterns, One Light
          </h2>
          <p className="leading-relaxed mb-6"
            style={{ color: 'rgba(212, 197, 171, 0.55)' }}>
            Across tradition after tradition, the same seven patterns appear.
            Not because someone copied someone else, but because they all seem to point at the same reality.
          </p>
          <p className="leading-relaxed mb-10"
            style={{ color: 'rgba(212, 197, 171, 0.55)' }}>
            This book maps those patterns, strips away the institutional layers, and asks:
            what if the mystics were all describing the same thing?
          </p>
          <Link
            to="/patterns"
            className="text-sm tracking-wide pb-1 transition-all"
            style={{
              color: 'rgba(201, 168, 76, 0.6)',
              borderBottom: '1px solid rgba(201, 168, 76, 0.25)',
            }}
          >
            Explore the Seven Patterns
          </Link>
        </div>
      </section>

      {/* Daily Reflection */}
      <section className="py-20 px-6" style={{ borderTop: '1px solid rgba(42, 37, 32, 0.5)' }}>
        <div className="max-w-xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-6">
            <Sparkles size={14} style={{ color: 'rgba(201, 168, 76, 0.5)' }} />
            <p className="text-xs uppercase tracking-[0.2em]"
              style={{ color: 'rgba(201, 168, 76, 0.5)' }}>
              {lang === 'cn' ? '今日反思' : "Today's Reflection"}
            </p>
            <Sparkles size={14} style={{ color: 'rgba(201, 168, 76, 0.5)' }} />
          </div>

          <blockquote className="font-serif text-xl md:text-2xl leading-relaxed mb-6"
            style={{ color: 'rgba(234, 225, 211, 0.8)' }}>
            {promptText}
          </blockquote>

          <p className="text-xs mb-8"
            style={{ color: 'rgba(201, 168, 76, 0.35)' }}>
            {lang === 'cn' ? `模式 ${daily.pattern}: ${patternLabel}` : `Pattern ${daily.pattern}: ${patternLabel}`}
          </p>

          <Link
            to={`/ask?q=${encodeURIComponent(promptText)}`}
            className="inline-flex items-center gap-2 text-sm tracking-wide transition-all"
            style={{
              color: 'rgba(201, 168, 76, 0.6)',
              borderBottom: '1px solid rgba(201, 168, 76, 0.25)',
            }}
          >
            <MessageCircle size={14} />
            {lang === 'cn' ? '用AI探索这个问题' : 'Explore this with AI'}
          </Link>
        </div>
      </section>

      {/* Quote */}
      <section className="py-20 px-6 relative">
        <div className="absolute inset-0 pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse at center, rgba(149,107,68,0.06) 0%, transparent 50%)',
          }}
        />
        <div className="max-w-xl mx-auto text-center relative">
          <blockquote className="font-serif text-xl md:text-2xl italic leading-relaxed"
            style={{ color: 'rgba(201, 168, 76, 0.6)' }}>
            "Every religion started as a campfire and became a fortress. This book keeps the fire."
          </blockquote>
        </div>
      </section>

      {/* Newsletter Signup */}
      <section className="py-24 px-6"
        style={{ borderTop: '1px solid rgba(42, 37, 32, 0.5)' }}>
        <div className="max-w-2xl mx-auto">
          <NewsletterSignup />
        </div>
      </section>

      {/* Going Through Something */}
      <section className="py-24 px-6"
        style={{ borderTop: '1px solid rgba(42, 37, 32, 0.5)' }}>
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="font-serif text-2xl mb-6"
            style={{ color: '#eae1d3' }}>
            {lang === 'cn' ? '你正在经历什么？' : 'Going Through Something?'}
          </h2>
          <p className="leading-relaxed mb-8"
            style={{ color: 'rgba(212, 197, 171, 0.5)' }}>
            {lang === 'cn'
              ? '悲伤、孤独、焦虑、信仰的失去——这些不是你需要修复的缺陷。它们是你可以探索的门。找到与你产生共鸣的模式和练习。'
              : "Grief, loneliness, anxiety, loss of faith — these aren't flaws to fix. They're doorways to explore. Find the pattern and practice that speaks to where you are."}
          </p>
          <Link
            to="/guidance"
            className="inline-flex items-center gap-2 text-sm tracking-wide pb-1 transition-all"
            style={{
              color: 'rgba(201, 168, 76, 0.6)',
              borderBottom: '1px solid rgba(201, 168, 76, 0.25)',
            }}
          >
            {lang === 'cn' ? '找到你的指引' : 'Find Your Guidance'}
          </Link>
        </div>
      </section>

      {/* AI Life Guide CTA */}
      <section className="py-24 px-6"
        style={{ borderTop: '1px solid rgba(42, 37, 32, 0.5)' }}>
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="font-serif text-2xl mb-6"
            style={{ color: '#eae1d3' }}>
            {lang === 'cn' ? '问AI人生向导' : 'Ask the AI Life Guide'}
          </h2>
          <p className="leading-relaxed mb-8"
            style={{ color: 'rgba(212, 197, 171, 0.5)' }}>
            {lang === 'cn'
              ? '关于人生、意义、苦难、爱或目的有疑问？与AI人生向导对话，扎根于众多传统的智慧。没有评判。没有教条。只有真诚。'
              : 'Have a question about life, meaning, suffering, love, or purpose? Talk to the AI Life Guide, grounded in the wisdom of many traditions. No judgment. No dogma. Just honesty.'}
          </p>
          <Link
            to="/ask"
            className="inline-flex items-center gap-2 px-8 py-3 transition-all rounded-sm text-sm tracking-wide"
            style={{
              backgroundColor: 'rgba(149, 107, 68, 0.12)',
              border: '1px solid rgba(149, 107, 68, 0.35)',
              color: '#c9a84c',
            }}
          >
            <MessageCircle size={16} />
            {lang === 'cn' ? '开始对话' : 'Start a Conversation'}
          </Link>
        </div>
      </section>
    </div>
  );
}
